import { useState, useEffect, useRef, useMemo } from 'react';
import {useDispatch, useSelector} from 'react-redux';
import { useParams } from 'react-router-dom';
import { Row, Col, Card, Button } from 'reactstrap';
import { useMutation, useQueryClient } from 'react-query';
import Select from 'react-select';
import { FaRegStar, FaStar, FaFilter } from 'react-icons/fa';
import { IoMdInformationCircle } from 'react-icons/io';
import { InputLabel, Tooltip } from '@mui/material';
import { AutoSizer, Column, Table } from 'react-virtualized';
import EmptyDataMessage from '../../../sharedComponents/ErrorMessages/EmptyDataMessage';
import { Theme, selectStyles } from '../../../config/constants';
import reportingApi from '../../../api/reportingApi';
import LocalStorage from '../../../sharedComponents/LocalStorage/LocalStorage';
import formatters from '../../../config/formatters';
import { Pagination } from '@mantine/core';
import useReports from '../useReports';
import { showModal } from '../../../reducers/ModalsReducer/ModalsActions';
import ShareReportModal from '../Modals/ShareReportModal';
import SaveAsCopyModal from '../Modals/SaveAsCopyModal';
import { FaArrowLeft, FaTrash, FaRegEdit } from 'react-icons/fa';
import moment from 'moment';
import DeleteReportModal from '../Modals/DeleteReportModal';
import { useLocation, useNavigate } from 'react-router-dom';
import { BsClipboardDataFill } from 'react-icons/bs';
import { getLastCompletedQuarterDates, getTrailing13MonthsDates } from '../../../utils/dateUtils';
import PageHeader from '../../../sharedComponents/Header/PageHeader';
import ManufacturerSelect from "../../../sharedComponents/ManufacturerSelect/ManufacturerSelect";
import FiltersWidget from '../../../sharedComponents/Filters/FiltersWidget';

const ReportWidget = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    
    const { exportReport,
        isExporting,
        useReportsList,
        useReportContent,
        portalTypeReportsList,
        recentReportsList,
        userReportsList,
        favoritedReportsList,
        selectedReportPage,
        setSelectedReportPage,
        getSelectedReportMetadata } = useReports();

    // React Query Mutations
    const updateReportMutation = useMutation({
        mutationFn: ({ id, params }) => reportingApi.updateReport.patch(id, params),
        onSuccess:async (response) => {
            if (response.data) {
                setSelectedReportMetadata(response.data);
                queryClient.invalidateQueries((['reportsList', 'reportContent']));
                queryClient.removeQueries({ queryKey: 'reportContent' });
            }
        },
        onError: (error) => {
            console.error('Error updating report:', error);
        }
    });

    const updateReportNameMutation = useMutation({
        mutationFn:async ({ id, name }) => await reportingApi.updateReport.patch(id, { name }),
        onSuccess: (response, variables) => {
            setSelectedReportMetadata(response.data);
            queryClient.invalidateQueries((['reportsList']));
            setIsEditingName(false);
        },
        onError: (error) => {
            console.error('Error updating report name:', error);
            setIsEditingName(false);
        }
    });

    const updateReportFavoriteMutation = useMutation({
        mutationFn: ({ id, favourite }) => reportingApi.updateReport.patch(id, { favourite }),
        onSuccess: (response, variables) => {
            setIsStarred(variables.favourite);
        },
        onError: (error) => {
            console.error('Error updating report favorite status:', error);
        }
    });
    const { reportId } = useParams();
    const { data: reportsList, isFetched: reportsListFetched } = useReportsList();
    const selectedManufacturers = useSelector(({user}) => user.selectedManufacturers);
    const { data: selectedReportContents, isLoading: reportLoading } = useReportContent(reportId, selectedReportPage, selectedManufacturers);
    const [selectedReportMetadata, setSelectedReportMetadata] = useState(getSelectedReportMetadata(reportId, reportsList));
    const prechosenFilters = {
        startDate: selectedReportMetadata?.filters?.startDate,
        endDate: selectedReportMetadata?.filters?.endDate,
        ...selectedReportMetadata?.filters
    };
    const [columnKeys, setColumnKeys] = useState([]);
    const mfrId = LocalStorage.manufacturerData.get().platform_id;
    const [startDate, endDate] = getTrailing13MonthsDates();
    const [isStarred, setIsStarred] = useState(selectedReportMetadata?.favourite || false);
    const [isReportHovered, setIsReportHovered] = useState(false);
    const [reportSelectValue, setReportSelectValue] = useState(null);
    const { numberFormatter, percentFormatter, usCurrencyFormatter } = formatters();
    const tableRef = useRef(null);
    const [isEditingName, setIsEditingName] = useState(false);
    const [editedName, setEditedName] = useState('');
    const inputRef = useRef(null);
    const [sortField, setSortField] = useState(selectedReportMetadata?.sortField ?? null);
    const [sortType, setSortType] = useState(selectedReportMetadata?.sortType ?? null);
    const dateRanges = {
        'Last 30 Days': [moment().subtract(29, 'days'), moment()],
        'Last 2 Months': [moment().subtract(2, 'month'), moment()],
        'Last Year': [moment().subtract(1, 'year'), moment()]
    };
    const currentParentPage = useMemo(() => {
        const locationPath = location.pathname;
        if (locationPath.includes('allreports')) {
            return 'allreports';
        } else if (locationPath.includes('recentreports')) {
            return 'recentreports'
        } else if (locationPath.includes('userreports')) {
            return 'userreports';
        } else if (locationPath.includes('favoritereports')) {
            return'favoritereports';
        } else {
            return 'allreports';
        }
    }, [location]);

    const reportSelectOptions = useMemo(() => {
        let reportsListOptions = [];
        switch(currentParentPage) {
        case ('allreports') :
            reportsListOptions = portalTypeReportsList;
            break;
        case ('recentreports') :
            reportsListOptions = recentReportsList;
            break;
        case ('userreports') :
            reportsListOptions = userReportsList;
            break;
        case ('favoritereports') :
            reportsListOptions = favoritedReportsList;
            break;
        default :
            break;
        }
        return reportsListOptions?.map((report) => ({
            label: report.name,
            value: report.id,
        }));
        // eslint-disable-next-line
    }, [reportsListFetched]);

    const handleSelectChange = (report) => {
        const selectedReportOption = reportSelectOptions?.find((reportOption) => reportOption.value === report.id);
        setReportSelectValue(selectedReportOption);
        queryClient.removeQueries((['reportContent']));
        setSelectedReportMetadata(getSelectedReportMetadata(report.value, reportsList));
        navigate('/' + currentParentPage + '/details/' + report.value);
    };

    const handleReportChange = (report) => {
        const selectedReportMetadata = reportSelectOptions?.find((reportOption) => reportOption.value === report.id);
        setReportSelectValue(selectedReportMetadata);
        navigate('/' + currentParentPage + '/details/' + report.id);
    };

    const handleShareClick = () => {
        const modalParams = {
            reportId: selectedReportMetadata?.id
        }
        dispatch(showModal(ShareReportModal, modalParams));
    };

    const handleStarClick = () => {
        updateReportFavoriteMutation.mutate({
            id: selectedReportMetadata?.id,
            favourite: !isStarred
        });
    };

    const handleCopyReport = () => {
        let selectedDates;
        if (selectedReportMetadata?.reportType?.name === 'Opportunity Monthly View Report') {
            selectedDates = getLastCompletedQuarterDates();
        } else {
            selectedDates = getTrailing13MonthsDates();
        }

        const modalParams = {
            parentPage: currentParentPage,
            reportTypeId: selectedReportMetadata?.reportType?.id,
            selectedDates,
            selectedCategories: selectedReportMetadata?.filters?.categories || [],
            selectedOperators: selectedReportMetadata?.filters?.parentGroups || [],
            selectedProducts: selectedReportMetadata?.filters?.products || [],
            selectedSegments: selectedReportMetadata?.filters?.marketSegments || [],
            selectedChildren: selectedReportMetadata?.filters?.children || []
        }
        dispatch(showModal(SaveAsCopyModal, modalParams));
    };

    const handleDeleteReport = () => {
        const modalParams = {
            reportId: selectedReportMetadata?.id,
            parentPage: currentParentPage
        }
        dispatch(showModal(DeleteReportModal, modalParams));
    };

    const patchReport = (newSortField, newSortType, filters) => {
        const params = {
            name: selectedReportMetadata.name,
            favourite: selectedReportMetadata.favourite,
            reportTypeId: selectedReportMetadata.reportType.reportTypeId,
            filters: {
                dateRange: [
                    moment(filters.startDate).startOf('month').format('YYYY-MM-DD'),
                    moment(filters.endDate).format('YYYY-MM-DD')
                ],
                parentGroups: filters.locations || [],
                marketSegments: filters.segments || [],
                ...filters
            },
            sortField: newSortField ?? sortField ?? selectedReportMetadata.sortField ?? 'total_volume',
            sortType: newSortType ?? sortType ?? selectedReportMetadata.sortType ?? 'desc'
        };
        updateReportMutation.mutate({
            id: selectedReportMetadata.id,
            params
        }, {
            onSuccess: () => {
                setSortField(params.sortField);
                setSortType(params.sortType);
            }
        });
    };

    const handleColumnHeaderClick = (dataKey) => {
        let key = formatters().camelCaseToUnderscore(dataKey);
        let newSortType = 'desc';
        if (sortField === key) {
            newSortType = sortType === 'desc' ? 'asc' : 'desc';
        }
        patchReport(key, newSortType);
    };

    const widgetColumns = columnKeys?.map((key, index) => {
        let isSorted = sortField === formatters().camelCaseToUnderscore(key);
        const sortIndicator = isSorted ? (sortType === 'asc' ? ' ▲' : ' ▼') : '';
        const headerRenderer = ({ label, dataKey }) => (
            <span
                style={{ cursor: 'pointer' }}
                onClick={() => handleColumnHeaderClick(dataKey)}
            >
                {label}{sortIndicator}
            </span>
        );
        if (key === 'percentageOfSales') {
            return (
                <Column
                    key={index}
                    label={formatters().camelToTitleCase(key)}
                    dataKey={key}
                    width={tableRef?.current?.props?.width / columnKeys?.length}
                    cellRenderer={({ cellData }) => <span>{percentFormatter.format(cellData / 100)}</span>}
                    headerRenderer={headerRenderer}
                />
            )
        } else if (key === 'totalVolume') {
            return (
                <Column
                    key={index}
                    label={formatters().camelToTitleCase(key)}
                    dataKey={key}
                    width={tableRef?.current?.props?.width / columnKeys?.length}
                    cellRenderer={({ cellData }) => <span>{usCurrencyFormatter.format(cellData)}</span>}
                    headerRenderer={headerRenderer}
                />
            )
        } else if (key === 'customerGroupId') {
            return (
                <Column
                    key={index}
                    label={formatters().camelToTitleCase(key)}
                    dataKey={key}
                    width={tableRef?.current?.props?.width / columnKeys?.length}
                    cellRenderer={({ cellData }) => <span>{cellData}</span>}
                />
            )
        }else if (key === 'manufacturerItemNumber') {
            if (!selectedReportMetadata?.ownerId) {
                return (
                    <Column
                        key={index}
                        label={'MIN'}
                        dataKey={key}
                        width={tableRef?.current?.props?.width / columnKeys?.length}
                        cellRenderer={({ cellData }) => <span>{cellData}</span>}
                    />
                )
            } else {
                return (
                    <Column
                        key={index}
                        label={'MIN'}
                        dataKey={key}
                        width={tableRef?.current?.props?.width / columnKeys?.length}
                        cellRenderer={({ cellData }) => <span>{cellData}</span>}
                        headerRenderer={headerRenderer}
                    />
                )
            }
        } else {
            if (!selectedReportMetadata?.ownerId) {
                return (
                    <Column
                        key={index}
                        label={formatters().camelToTitleCase(key)}
                        dataKey={key}
                        cellRenderer={({ cellData }) => isNaN(cellData) ? <span>{cellData}</span> : <span>{numberFormatter.format(Math.round(cellData))}</span>}
                        width={tableRef?.current?.props?.width / columnKeys?.length}
                    />
                )
            } else {
                return (
                    <Column
                        key={index}
                        label={formatters().camelToTitleCase(key)}
                        dataKey={key}
                        width={tableRef?.current?.props?.width / columnKeys?.length}
                        cellRenderer={({ cellData }) => isNaN(cellData) ? <span>{cellData}</span> : <span>{numberFormatter.format(Math.round(cellData))}</span>}
                        headerRenderer={headerRenderer}
                    />
                )
            }
        }
    });

    const handleClickBack = () => {
        navigate('/' + currentParentPage);
    };

    const handleEditNameClick = () => {
        setEditedName(selectedReportMetadata?.name || '');
        setIsEditingName(true);
    };

    const handleEditNameChange = (e) => {
        setEditedName(e.target.value);
    };

    const handleEditNameBlur = () => {
        saveEditedName();
    };

    const handleEditNameKeyDown = (e) => {
        if (e.key === 'Enter') {
            saveEditedName();
        } else if (e.key === 'Escape') {
            setIsEditingName(false);
        }
    };

    const saveEditedName = () => {
        if (!editedName.trim() || editedName === selectedReportMetadata?.name) {
            setIsEditingName(false);
            return;
        }
        updateReportNameMutation.mutate({
            id: selectedReportMetadata?.id,
            name: editedName
        });
    };

    useEffect(() => {
        if (selectedReportMetadata) {
            handleReportChange(selectedReportMetadata);
        }
        // eslint-disable-next-line
    }, [selectedReportMetadata]);

    useEffect(() => {
        if (isEditingName && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditingName]);

    useEffect(() => {
        if (reportsListFetched) {
            setSelectedReportMetadata(getSelectedReportMetadata(reportId, reportsList));
            setReportSelectValue(reportSelectOptions?.find((option) => option.value === +reportId));
        }
    // eslint-disable-next-line
    }, [reportId, reportsListFetched]);

    useEffect(() => {
        if (selectedReportContents?.data?.length > 0) {
            setColumnKeys(Object.keys(selectedReportContents?.data?.[0]));
        }
    }, [selectedReportContents]);

    return (
        <Row>
            <Col>
                <PageHeader title='Reports' subtitle={selectedReportMetadata?.name} icon={<BsClipboardDataFill fill='url(#icon-gradient)'/>}/>
                <Row style={{marginTop: '16px'}}>
                    <Col className='main-header text-size-14 w-fc' style={{fontWeight: '700', letterSpacing:'.0025em', lineHeight: '16px', cursor: 'pointer'}} onClick={() => handleClickBack()}>
                        <FaArrowLeft color={Theme().colors.accent} size={'14'} style={{position: 'relative', top: '-1px'}}/><span style={{color: Theme().colors.accent}}> Back</span>
                    </Col>
                </Row>
                <Row className='label-text text-size-14 d-flex align-items-center mx-auto pb-2 mt-2'>
                    <Col className='ps-0 col-3'>
                        <InputLabel style={{color: 'black', fontSize: '12px'}}>Now Viewing</InputLabel>
                        <Select placeholder='Select Report' options={reportSelectOptions} onChange={(report) => handleSelectChange(report)} value={reportSelectValue} styles={selectStyles} />
                    </Col>
                    <Col className='ps-1 col-3 mt-auto'>
                        <ManufacturerSelect align="start" />
                    </Col>
                    <Col></Col>
                    <Col className='w-fc'>
                        <Button
                        outline
                        className='py-2 button-label'
                        size='sm'
                        style={{width: '70px', top: '16px', position: 'relative'}}
                        onClick={() => handleShareClick()}
                        color={Theme().colors.accentName}>Share
                        </Button>
                    </Col>
                    <Col className='w-fc'>
                        <Button
                            outline
                            className='py-2 button-label'
                            size='sm'
                            style={{width: '70px', top: '16px', position: 'relative'}}
                            onClick={() => exportReport(selectedReportMetadata?.id, selectedReportMetadata?.name, mfrId)}
                            disabled={isExporting}
                            color={Theme().colors.accentName}
                        >
                            Export
                        </Button>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Card className='p-3' onMouseEnter={() => setIsReportHovered(true)} onMouseLeave={() => setIsReportHovered(false)}>
                            <Row style={{minHeight: 'calc(100vh - 180px)'}}>
                                <Col style={{display: 'flex', flexDirection: 'column'}}>
                                    <Row>
                                        <Col className='w-fc pe-0'>
                                            {isStarred ? (
                                                <FaStar
                                                    size={'20px'}
                                                    color={Theme().colors.accent}
                                                    onClick={handleStarClick}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            ) : (
                                                <FaRegStar
                                                    size={'20px'}
                                                    color={'black'}
                                                    onClick={handleStarClick}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            )}
                                        </Col>
                                        <Col className='w-fc pe-0'>
                                            <div
                                                className='main-header text-size-20'
                                                style={{
                                                    lineHeight: '23px',
                                                    fontWeight: '700',
                                                    letterSpacing: '.0025em',
                                                    cursor: isReportHovered && selectedReportMetadata?.ownerId ? 'pointer' : 'default',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    minHeight: '28px'
                                                }}
                                                onClick={() => {
                                                    if (isReportHovered && selectedReportMetadata?.ownerId) handleEditNameClick();
                                                }}
                                                onMouseOver={() => setIsReportHovered(true)}
                                                onMouseOut={() => setIsReportHovered(false)}
                                            >
                                                {isEditingName ? (
                                                    <input
                                                        ref={inputRef}
                                                        type="text"
                                                        value={editedName}
                                                        onChange={handleEditNameChange}
                                                        onBlur={handleEditNameBlur}
                                                        onKeyDown={handleEditNameKeyDown}
                                                        style={{
                                                            fontSize: '20px',
                                                            fontWeight: '700',
                                                            width: '100%',
                                                            border: '1px solid #ccc',
                                                            borderRadius: '4px',
                                                            padding: '2px 6px'
                                                        }}
                                                        maxLength={100}
                                                    />
                                                ) : (
                                                    <span
                                                        title={selectedReportMetadata?.ownerId ? "Click to edit name" : undefined}
                                                        style={{ display: 'flex', alignItems: 'center', width: '100%' }}
                                                    >
                                                        {selectedReportMetadata?.name}
                                                        {isReportHovered && selectedReportMetadata?.ownerId && (
                                                            <FaRegEdit
                                                                style={{
                                                                    marginLeft: 8,
                                                                    color: Theme().colors.accent,
                                                                    fontSize: 18,
                                                                    verticalAlign: 'middle'
                                                                }}
                                                                title="Edit report name"
                                                            />
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        </Col>
                                        {!selectedReportMetadata?.ownerId &&
                                        <Col className='w-fc px-1'>
                                            <Tooltip title={"Any changes to the filters on this report will not be saved. Please make a copy of the report to make permanent changes."} placement="top">
                                                <div>
                                                    <IoMdInformationCircle style={{fontSize: "18px", color: Theme().colors.accent, position: 'relative', bottom: '2px'}} />
                                                </div>
                                            </Tooltip>
                                        </Col>
                                        }
                                        <Col></Col>
                                        {!selectedReportMetadata?.ownerId &&
                                        <Col className='w-fc'>
                                            <Button
                                                block
                                                className='button-label'
                                                size='sm'
                                                style={{width: '70px'}}
                                                onClick={() => handleCopyReport()}
                                                color={Theme().colors.accentName}>Save As Copy
                                            </Button>
                                        </Col>
                                        }
                                        {(isReportHovered && selectedReportMetadata?.ownerId) &&
                                        <Col className='w-fc'>
                                            <Button
                                                block
                                                className='button-label'
                                                style={{height: '24px', width: '24px'}}
                                                title='Delete Report'
                                                size='sm'
                                                color='redLight'
                                                onClick={() => {handleDeleteReport()}}>
                                                <FaTrash color='white' style={{top: '-5px', position: 'relative'}} />
                                            </Button>
                                        </Col>
                                        }
                                    </Row>
                                    {!selectedReportMetadata?.ownerId &&
                                    <Card className='p-2' style={{background:'url("./FiltersBackground.png")', backgroundRepeat: 'no-repeat', backgroundSize: 'cover'}}>
                                        <Row className='mb-2'>
                                            <div className="main-header text-size-20" style={{color: '#00A6CC'}}><FaFilter fontSize={'15px'} style={{marginBottom: '5px'}}/> Filters</div>
                                        </Row>
                                        <Row>
                                            <Col>
                                                <div className='main-header' style={{textAlign: 'center', height: '38px'}}>Please copy report first to use filters</div>
                                            </Col>
                                        </Row>
                                    </Card>
                                    }
                                    {selectedReportMetadata?.ownerId &&
                                    <FiltersWidget
                                        fromDate={startDate}
                                        toDate={endDate}
                                        minDate={startDate}
                                        maxDate={endDate}
                                        dateRanges={dateRanges}
                                        prechosenFilters={prechosenFilters}
                                        datasetOptionsEnabled={false}
                                        productOptionsEnabled={true}
                                        saveFilterSetsEnabled={false}
                                        filterSetOptionsEnabled={false}
                                        onApplyFilters={(filters) => {
                                            patchReport(null, null, filters);
                                        }}
                                    />
                                    }
                                    {selectedReportContents?.data?.length > 0 && !reportLoading &&
                                    <Row style={{flex: '1', minHeight: '300px'}}>
                                        <Col style={{height: '100%'}}>
                                            <AutoSizer>
                                                {({ width, height }) => (
                                                    <Table className="table reportsTable border-grayLight"
                                                    width={width}
                                                    height={height}
                                                    headerHeight={34.5}
                                                    rowHeight={38}
                                                    rowCount={selectedReportContents?.data?.length}
                                                    rowGetter={({ index }) => selectedReportContents?.data?.[index]}
                                                    ref={tableRef}
                                                    >
                                                        {widgetColumns}
                                                    </Table>
                                                )}
                                            </AutoSizer>
                                        </Col>
                                    </Row>
                                    }
                                    {reportLoading &&
                                    <Row style={{height: 'calc(100% - 180px)'}}>
                                        <Col className='d-flex justify-content-center align-items-center h-100'>
                                            <div className='spinner-border text-primary' role='status'>
                                                <span className='visually-hidden'>Loading...</span>
                                            </div>
                                        </Col>
                                    </Row>
                                    }
                                    {selectedReportContents?.data?.length <= 0 && !reportLoading &&
                                    <Row style={{height: 'calc(100% - 180px)'}}>
                                        <Col>
                                            <EmptyDataMessage />
                                        </Col>
                                    </Row>
                                    }
                                    <Row className='mx-1' style={{borderTop: '1px solid lightgray', paddingTop: '12px'}}>
                                        <Col className='w-fc mx-auto'>
                                            <Pagination withEdges={true} total={selectedReportContents?.totalPages} onChange={(page) => setSelectedReportPage(page)} color={Theme().colors.secondary}/>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                </Row>
            </Col>
        </Row>
    );
}

export default ReportWidget;
